/**
 * Dynamically imports and renders various React components based on their type.
 * 
 * The file defines a set of components using `react-loadable` for code-splitting
 * and lazy loading. Each component is associated with a specific type and is rendered
 * conditionally based on the `__component` property of the input data.
 * 
 * Functions:
 * - `renderLayout`: Maps over an array of components and renders each one using `renderLayoutByType`.
 * - `renderLayoutByType`: Switches on the `__component` property to render the appropriate React component.
 * 
 * Components:
 * - `PageCarousel`: Lazy-loaded component for rendering a page carousel.
 * - `HomeCarousel`: Lazy-loaded component for rendering a home page carousel.
 * - `CallToAction`: Lazy-loaded component for rendering a call-to-action section.
 * - `PageVideo`: Lazy-loaded component for rendering a video section.
 * - `Grid1`, `Grid2`, `Grid3`, `Grid4`: Lazy-loaded components for rendering grid layouts with varying column configurations.
 * - `PageFlashsale`: Lazy-loaded component for rendering a flash sale section.
 * - `HomeBlogs`: Lazy-loaded component for rendering a blogs section on the home page.
 * - `HomeCategories`: Lazy-loaded component for rendering categories on the home page.
 * - `HomeBestOfTheWeek`: Lazy-loaded component for rendering the "Best of the Week" section.
 * - `HomeRecommend`: Lazy-loaded component for rendering recommended items on the home page.
 * - `SISHeader`, `SISBanner`, `SISText`: Lazy-loaded components for rendering shop-in-shop headers, banners, and text sections.
 * - `AdsA`, `AdsB`, `AdsC`, `AdsD`: Lazy-loaded components for rendering various advertisement layouts.
 * - `SISTopProducts`: Lazy-loaded component for rendering top products in a shop-in-shop section.
 * - `SISProducts`: Lazy-loaded component for rendering product categories in a shop-in-shop section.
 * 
 * Usage:
 * - Use `renderLayout` to render a list of components dynamically based on their type.
 * - Ensure that the input data contains a `__component` property to determine the type of component to render.
 * 
 * Notes:
 * - The `react-loadable` library is used for lazy loading components, improving performance by splitting the code.
 * - The `lodash` library is used for utility functions like `_.map` and `_.get`.
 */
import React from 'react';
import _ from 'lodash'
// import {Link} from 'react-router-dom';
import Loadable from 'react-loadable';

const PageCarousel = Loadable({
    loader: () => import(/* webpackChunkName: "PageCarousel"*/
        '../components/strapi/page/Carousel'),
    loading: () => null,
    modules: ['PageCarousel']
});

const HomeCarousel = Loadable({
    loader: () => import(/* webpackChunkName: "HomeCarousel"*/
        '../components/strapi/home/HomeCarousel'),
    loading: () => null,
    modules: ['HomeCarousel']
});

const CallToAction = Loadable({
    loader: () => import(/* webpackChunkName: "CallToAction"*/
        '../components/common/CallToAction'),
    loading: () => null,
    modules: ['CallToAction']
});

const PageVideo = Loadable({
    loader: () => import(/* webpackChunkName: "Video"*/
        '../components/strapi/page/Video'),
    loading: () => null,
    modules: ['Video']
});

const Grid1 = Loadable({
    loader: () => import(/* webpackChunkName: "Grid1"*/
        '../components/strapi/page/Grid1'),
    loading: () => null,
    modules: ['Grid1']
});

const Grid2 = Loadable({
    loader: () => import(/* webpackChunkName: "Grid2"*/
        '../components/strapi/page/Grid2'),
    loading: () => null,
    modules: ['Grid2']
});

const Grid3 = Loadable({
    loader: () => import(/* webpackChunkName: "Grid3"*/
        '../components/strapi/page/Grid3'),
    loading: () => null,
    modules: ['Grid3']
});

const Grid4 = Loadable({
    loader: () => import(/* webpackChunkName: "Grid4"*/
        '../components/strapi/page/Grid4'),
    loading: () => null,
    modules: ['Grid4']
});

const PageFlashsale = Loadable({
    loader: () => import(/* webpackChunkName: "PageFlashsale"*/
        '../components/strapi/page/Flashsale'),
    loading: () => null,
    modules: ['PageFlashsale']
});

const HomeBlogs = Loadable({
    loader: () => import(/* webpackChunkName: "HomeBlogs"*/
        '../components/strapi/home/Blogs'),
    loading: () => null,
    modules: ['HomeBlogs']
});

const HomeCategories = Loadable({
    loader: () => import(/* webpackChunkName: "HomeCategories"*/
        '../components/strapi/home/Categories'),
    loading: () => null,
    modules: ['HomeCategories']
});

const HomeBestOfTheWeek = Loadable({
    loader: () => import(/* webpackChunkName: "HomeBestOfTheWeek"*/
        '../components/strapi/home/BestSeller'),
    loading: () => null,
    modules: ['HomeBestOfTheWeek']
});

const HomeRecommend = Loadable({
    loader: () => import(/* webpackChunkName: "HomeRecommend"*/
        '../components/strapi/home/Recommend'),
    loading: () => null,
    modules: ['HomeRecommend']
});

const SISHeader = Loadable({
    loader: () => import(/* webpackChunkName: "SISHeader"*/
        '../components/strapi/shop-in-shop/Header'),
    loading: () => null,
    modules: ['SISHeader']
});

const SISBanner = Loadable({
    loader: () => import(/* webpackChunkName: "SISBanner"*/
        '../components/strapi/shop-in-shop/Banner'),
    loading: () => null,
    modules: ['SISBanner']
});

const SISText = Loadable({
    loader: () => import(/* webpackChunkName: "SISText"*/
        '../components/strapi/shop-in-shop/Text'),
    loading: () => null,
    modules: ['SISText']
});

const AdsA = Loadable({
    loader: () => import(/* webpackChunkName: "AdsA"*/
        '../components/strapi/shop-in-shop/AdsA'),
    loading: () => null,
    modules: ['AdsA']
});

const AdsB = Loadable({
    loader: () => import(/* webpackChunkName: "AdsB"*/
        '../components/strapi/shop-in-shop/AdsB'),
    loading: () => null,
    modules: ['AdsB']
});

const AdsC = Loadable({
    loader: () => import(/* webpackChunkName: "AdsC"*/
        '../components/strapi/shop-in-shop/AdsC'),
    loading: () => null,
    modules: ['AdsC']
});

const AdsD = Loadable({
    loader: () => import(/* webpackChunkName: "AdsD"*/
        '../components/strapi/shop-in-shop/AdsD'),
    loading: () => null,
    modules: ['AdsD']
});

const SISTopProducts = Loadable({
    loader: () => import(/* webpackChunkName: "SISTopProducts"*/
        '../components/strapi/shop-in-shop/TopProducts'),
    loading: () => null,
    modules: ['SISTopProducts']
});

const SISProducts = Loadable({
    loader: () => import(/* webpackChunkName: "SISProducts"*/
        '../components/strapi/shop-in-shop/ProductCategories'),
    loading: () => null,
    modules: ['SISProducts']
});

const PageTabs = Loadable({
    loader: () => import(/* webpackChunkName: "PageTabs"*/
        '../components/strapi/page/Tabs'),
    loading: () => null,
    modules: ['PageTabs']
});

const PageProducts = Loadable({
    loader: () => import(/* webpackChunkName: "PageProducts"*/
        '../components/strapi/page/Products'),
    loading: () => null,
    modules: ['PageProducts']
});

const PageCoupons = Loadable({
    loader: () => import(/* webpackChunkName: "PageCoupons"*/
        '../components/strapi/page/Coupons'),
    loading: () => null,
    modules: ['PageCoupons']
});

/**
 * Renders a layout by mapping over an array of components and rendering each component
 * based on its type.
 *
 * @param {Array} components - An array of components to be rendered. Each component
 *                             should contain the necessary data to determine its type.
 * @returns {Array} An array of rendered layout elements.
 */
export const renderLayout = (components) => {
    if (!components || components.length === 0) {
        return null;
    }

    return (
        _.map(components, (c, index) => {
            try {
                return renderLayoutByType(c, index);
            } catch (error) {
                console.error(`Error rendering component ${c.__component} at index ${index}:`, error);
                return (
                    <div key={`error_${index}`} className="component-error">
                        <p>Component failed to load</p>
                    </div>
                );
            }
        })
    )
}

/**
 * Renders a React component based on the provided type.
 *
 * @param {Object} _com - The component data object containing the type and properties.
 * @param {string} _com.__component - The type of the component to render.
 * @returns {JSX.Element|null} The rendered React component or null if the type is not recognized.
 */
const renderLayoutByType = (_com) => {
    const key = `${_com.__component}_${_com.id}`;
    switch (_com.__component) {
        case "page.carousel":
            return <PageCarousel key={key} {..._com} banners={_com.banners} />
        case "home.home-carousel":
            return <HomeCarousel key={key} {..._com} initBanners={_com.banners} />
        case "home.categories":
            return <HomeCategories key={key} {..._com} categories={_com.Category} />
        case "page.blogs":
            return <HomeBlogs key={key} {..._com} highlight={_com.highlight_blog} />
        case "home.best-of-the-week":
            return <HomeBestOfTheWeek key={key} {..._com} item_ids={[_com.bt_code_1, _com.bt_code_2, _com.bt_code_3, _com.bt_code_4, _com.bt_code_5, _com.bt_code_6]} />
        case "home.recommend":
            return <HomeRecommend key={key} {..._com} id={_.get(_com, "home_recommend_section.id", null)} title={_.get(_com, "home_recommend_section.title", null)} background_color={_.get(_com, "home_recommend_section.background_color", null)} />
        case "page.call-to-action":
            return <CallToAction key={key} {..._com} />;
        case "page.flashsale":
            return <PageFlashsale key={key} {..._com} />;
        case "page.grid-1-column":
            return <Grid1 key={key} {..._com} title={_com.title} view_all_link={_com.view_all_link} bgColor={_com.bg_color} {..._com.cell} />;
        case "page.grid-2-columns":
            return <Grid2 key={key} {..._com} title={_com.title} view_all_link={_com.view_all_link} bgColor={_com.bg_color} cells={_com.cells} />;
        case "page.grid-3-columns-1-2":
            return <Grid3 key={key} {..._com} title={_com.title} view_all_link={_com.view_all_link} mobile_one_two={_com.mobile_one_two} columns_mobile={_com.columns_mobile} bgColor={_com.bg_color} cells={_com.cells} />;
        case "page.grid-4-columns":
            return <Grid4 key={key} {..._com} title={_com.title} view_all_link={_com.view_all_link} bgColor={_com.bg_color} cells={_com.cells} />;
        case "page.video":
            return <PageVideo key={key} {..._com} title={_com.title} video={_com.video} is_youtube={_com.is_youtube} youtube_id={_com.youtube_id} />;
        case "shop-in-shop.header":
            return <SISHeader key={key} {..._com.content} />
        case "shop-in-shop.banner":
            return <SISBanner key={key} {..._com} />
        case "shop-in-shop.text":
            return <SISText key={key} {..._com} />
        case "shop-in-shop.type-a":
            return <AdsA key={key} {..._com} />
        case "shop-in-shop.type-b-1-2":
            return <AdsB key={key} {..._com} />
        case "shop-in-shop.type-c-2-1-1-2":
            return <AdsC key={key} {..._com} />
        case "shop-in-shop.type-d-3-cols":
            return <AdsD key={key} {..._com} />
        case "shop-in-shop.top-products":
            return <SISTopProducts key={key} {..._com} />
        case "shop-in-shop.products":
            return <SISProducts key={key} {..._com} />
        case "page.tabs":
            return <PageTabs key={key} {..._com} />
        case "page.products":
            return <PageProducts key={key} {..._com} />
        case "page.coupons":
            return <PageCoupons key={key} {..._com} />
        default:
            return null
    }
}